import React from "react";

import { QuoteBreakdown } from "@/shared/components/payments/QuoteBreakdown";
import useTranslations from "@/shared/lang/useTranslations";
import { railLabelForCountry } from "@/shared/utils/railDisplay";

import { COUNTRIES } from "../../config/countries.config";
import { resolveColRail } from "../../constants/dispersions.constants";
import { useBrebKeyResolution } from "../../hooks/useBrebKeyResolution";
import { BrebKeyType } from "../../services/paymentsPayout.service";
import type { NativeQuote } from "../../services/paymentsQuotes.service";
import type { CountryCode } from "../../types/countryFields.types";
import type { DispersionFormData } from "../../types/dispersions-api.types";
import { maskBrebKey } from "../../utils/recipientDisplay";

// Faithful "Paso 3 · Revisión" design (Sereno theme): two summary cards
// (operation + beneficiary), wired to the real form + quote data.

interface Step3ConfirmationProps {
  formData: DispersionFormData;
  /** Currency of the wallet being debited — the origin is a wallet, not a country. */
  originCurrency: string;
  destinationCountry?: CountryCode;
  quoteData?: NativeQuote;
}

// Theme colors live in Tailwind classes (with dark variants).
const CARD_CLASS = "rounded-[12px] border border-border";
const ROW_LABEL_CLASS = "text-muted-foreground";

const PIX_TYPE_CANON: Record<string, string> = {
  "0": "CPF",
  "1": "CNPJ",
  "2": "EMAIL",
  "3": "PHONE",
  "4": "EVP",
};

function maskPix(type: string, key: string): string {
  const digits = key.replace(/\D/g, "");
  if (type === "CPF" && digits.length === 11)
    return digits.slice(0, 3) + "." + digits.slice(3, 6) + ".***-**";
  if (type === "CNPJ" && digits.length === 14)
    return digits.slice(0, 2) + "." + digits.slice(2, 5) + ".***/****-**";
  if (type === "EMAIL") {
    const parts = key.split("@");
    if (parts.length < 2) return key;
    return parts[0].slice(0, 2) + "***@" + parts[1];
  }
  if (type === "PHONE" && digits.length >= 10)
    return "(" + digits.slice(0, 2) + ") *****-" + digits.slice(-4);
  if (type === "EVP") return key.slice(0, 8) + "-****-****-…";
  return key || "—";
}

const ROW_CLASS = "flex justify-between text-sm";
const ROW_VALUE_CLASS = "font-medium";
const ROW_NUM_CLASS = "font-medium tabular-nums";
const CARD_BODY_CLASS = "flex flex-col gap-[11px] px-[22px] py-[18px]";
const CARD_TITLE_CLASS = "text-[11px] font-semibold tracking-wider uppercase text-muted-foreground";

// React.memo: only re-renders when the reviewed data actually changes (no typing happens on step 3).
export const Step3Confirmation: React.FC<Step3ConfirmationProps> = React.memo(function Step3Confirmation({
  formData,
  originCurrency,
  destinationCountry,
  quoteData,
}) {
  const { t } = useTranslations("dispersions");
  const destInfo = destinationCountry ? COUNTRIES[destinationCountry] : null;

  const rawPixType = String(formData.recipientFields?.pix_type ?? "");
  const pixTypeCanon = PIX_TYPE_CANON[rawPixType] ?? rawPixType.toUpperCase();
  // CPF/CNPJ (and unknown values) render as-is; the descriptive types are translated.
  const pixTypeLabel =
    pixTypeCanon === "EMAIL"
      ? t("step3_pix_type_email")
      : pixTypeCanon === "PHONE"
        ? t("step3_pix_type_phone")
        : pixTypeCanon === "EVP"
          ? t("step3_pix_type_evp")
          : pixTypeCanon || "—";
  const pixKey = String(formData.recipientFields?.pix_key ?? "");
  const clabe = String(formData.recipientFields?.clabe ?? "");
  // Same domain normalization as Step2 (ArgentinaCbuCvuRecipient): anything but CBU reads as CVU, so the
  // account-type row and the number label below can never disagree on an unexpected value.
  const arAccountType =
    String(formData.recipientFields?.account_type ?? "").toUpperCase() === "CBU" ? "CBU" : "CVU";
  const arAccountNumber = String(formData.recipientFields?.account_number ?? "");
  const arCuit = String(formData.recipientFields?.cuit ?? "").trim();
  const brebKeyType = String(formData.recipientFields?.breb_key_type ?? "").toUpperCase();
  const brebKeyValue = String(formData.recipientFields?.breb_key_value ?? "");
  const brebTypeLabel =
    brebKeyType === "PHONE"
      ? t("breb_key_type_phone")
      : brebKeyType === "EMAIL"
        ? t("breb_key_type_email")
        : brebKeyType === "ALPHANUM"
          ? t("breb_key_type_alphanum")
          : brebKeyType === "NRIC"
            ? t("breb_key_type_nric")
            : brebKeyType === "MERCHANT"
              ? t("breb_key_type_merchant")
              : "—";
  const beneficiary = String(formData.recipientFields?.beneficiary ?? "—");
  const concept = String(formData.recipientFields?.description ?? "").trim() || "—";

  const isMexicoDest = destInfo?.iso3 === "MEX";
  const isArgentinaDest = destInfo?.iso3 === "ARG";
  const isColombiaDest = destInfo?.iso3 === "COL";
  const isBrebDest = isColombiaDest && resolveColRail(formData.recipientFields) === "BREB";
  // The same directory answer step 2 showed. `useBrebKeyResolution` caches deterministic results by key, so
  // this resolves on the first render instead of blanking the holder rows for a debounce + round trip — and
  // a directory that goes down between the two steps can no longer erase an identity the user already
  // confirmed.
  const brebResolution = useBrebKeyResolution(
    (Object.values(BrebKeyType) as string[]).includes(brebKeyType)
      ? (brebKeyType as BrebKeyType)
      : BrebKeyType.PHONE,
    brebKeyValue,
    isBrebDest,
  );
  // Rail label comes from the shared source of truth (railDisplay); only the generic fallback is translated.
  const rail = destInfo ? railLabelForCountry(destInfo.iso3, t("rail_transfer")) : "—";

  return (
    <div className="flex flex-col gap-[18px] text-foreground">
      {/* Resumen de la operación */}
      <div className={`${CARD_CLASS} ${CARD_BODY_CLASS}`}>
        <div className={CARD_TITLE_CLASS}>{t("step3_summary_title")}</div>
        <div className={ROW_CLASS}>
          <span className={ROW_LABEL_CLASS}>{t("route_label")}</span>
          <span className={ROW_VALUE_CLASS}>
            {originCurrency} → {destInfo?.name ?? "—"}
          </span>
        </div>
        <div className={ROW_CLASS}>
          <span className={ROW_LABEL_CLASS}>{t("rail_label")}</span>
          <span className={ROW_VALUE_CLASS}>{rail}</span>
        </div>
        {quoteData ? (
          <QuoteBreakdown
            quote={quoteData}
            variant="rows"
            serviceFeeSuffix={destInfo?.name}
            labels={{
              send: t("send_label"),
              conversionFee: t("quote_conversion_fee_label"),
              rate: t("quote_exchange_rate_label"),
              converted: t("quote_converted_amount_label"),
              serviceFee: t("quote_dispersion_fee_label"),
              totalDebit: t("quote_total_debit_label"),
              receives: t("quote_recipient_receives_label"),
            }}
          />
        ) : (
          // No quote means nothing has been priced yet. The footer button is disabled by the step-3 validity
          // rule in `useDispersionFormErrors`, and the page renders an alert above saying why — this row only
          // has to avoid claiming an amount it does not have. (That rule did not exist when this comment was
          // first written, which is how the button stayed enabled here.)
          <div className={ROW_CLASS}>
            <span className={ROW_LABEL_CLASS}>{t("quote_recipient_receives_label")}</span>
            <span className={ROW_NUM_CLASS}>—</span>
          </div>
        )}
      </div>

      {/* Beneficiario */}
      <div className={`${CARD_CLASS} ${CARD_BODY_CLASS}`}>
        <div className={CARD_TITLE_CLASS}>{t("step3_recipient_title")}</div>
        {/* Bre-B has no typed name — the directory's holder row below IS the name. Keeping this row would
            print "Name: —" directly above it. Every other rail still shows what the user entered. */}
        {!isBrebDest && (
          <div className={ROW_CLASS}>
            <span className={ROW_LABEL_CLASS}>{t("step3_name_label")}</span>
            <span className={ROW_VALUE_CLASS}>{beneficiary}</span>
          </div>
        )}
        {/* Mexico / SPEI shows the destination CLABE in full and Argentina / CBU_CVU
            the 22-digit account — the user must verify them before an irreversible
            payout. Other corridors keep the PIX rows exactly as before. */}
        {isMexicoDest ? (
          <div className={ROW_CLASS}>
            <span className={ROW_LABEL_CLASS}>{t("step3_clabe_label")}</span>
            <span className={ROW_NUM_CLASS}>{clabe || "—"}</span>
          </div>
        ) : isArgentinaDest ? (
          <>
            <div className={ROW_CLASS}>
              <span className={ROW_LABEL_CLASS}>{t("step3_account_type_label")}</span>
              <span className={ROW_VALUE_CLASS}>{arAccountType}</span>
            </div>
            <div className={ROW_CLASS}>
              <span className={ROW_LABEL_CLASS}>{arAccountType}</span>
              <span className={ROW_NUM_CLASS}>{arAccountNumber || "—"}</span>
            </div>
            {arCuit && (
              <div className={ROW_CLASS}>
                <span className={ROW_LABEL_CLASS}>{t("step3_cuit_label")}</span>
                <span className={ROW_NUM_CLASS}>{arCuit}</span>
              </div>
            )}
          </>
        ) : isColombiaDest ? (
          resolveColRail(formData.recipientFields) === "TRANSFIYA" ? (
            <>
              <div className={ROW_CLASS}>
                <span className={ROW_LABEL_CLASS}>{t("step3_tf_bank_label")}</span>
                <span className={ROW_VALUE_CLASS}>
                  {String(formData.recipientFields?.transfiya_bank_name ?? "") ||
                    String(formData.recipientFields?.transfiya_bank_code ?? "—")}
                </span>
              </div>
              <div className={ROW_CLASS}>
                <span className={ROW_LABEL_CLASS}>{t("step3_tf_account_label")}</span>
                <span className={ROW_NUM_CLASS}>
                  {String(formData.recipientFields?.transfiya_account_number ?? "—")}
                </span>
              </div>
              <div className={ROW_CLASS}>
                <span className={ROW_LABEL_CLASS}>{t("step3_tf_doc_label")}</span>
                <span className={ROW_NUM_CLASS}>
                  {`${String(formData.recipientFields?.transfiya_document_type ?? "")} ${String(
                    formData.recipientFields?.transfiya_document_number ?? "—",
                  )}`.trim()}
                </span>
              </div>
            </>
          ) : (
            <>
              <div className={ROW_CLASS}>
                <span className={ROW_LABEL_CLASS}>{t("step3_breb_key_type_label")}</span>
                <span className={ROW_VALUE_CLASS}>{brebTypeLabel}</span>
              </div>
              <div className={ROW_CLASS}>
                <span className={ROW_LABEL_CLASS}>{t("step2_breb_key_label")}</span>
                <span className={ROW_NUM_CLASS}>{maskBrebKey(brebKeyType, brebKeyValue)}</span>
              </div>
              {brebResolution.status === "resolved" && (
                <>
                  <div className={ROW_CLASS}>
                    <span className={ROW_LABEL_CLASS}>{t("step3_breb_holder_label")}</span>
                    <span className={ROW_VALUE_CLASS}>{brebResolution.data.holder_name || "—"}</span>
                  </div>
                  {brebResolution.data.bank_name && (
                    <div className={ROW_CLASS}>
                      <span className={ROW_LABEL_CLASS}>{t("step3_breb_bank_label")}</span>
                      <span className={ROW_VALUE_CLASS}>{brebResolution.data.bank_name}</span>
                    </div>
                  )}
                </>
              )}
            </>
          )
        ) : (
          <>
            <div className={ROW_CLASS}>
              <span className={ROW_LABEL_CLASS}>{t("step3_pix_type_label")}</span>
              <span className={ROW_VALUE_CLASS}>{pixTypeLabel}</span>
            </div>
            <div className={ROW_CLASS}>
              <span className={ROW_LABEL_CLASS}>{t("step3_pix_key_label")}</span>
              <span className={ROW_NUM_CLASS}>{maskPix(pixTypeCanon, pixKey)}</span>
            </div>
          </>
        )}
        <div className={ROW_CLASS}>
          <span className={ROW_LABEL_CLASS}>{t("concept_label")}</span>
          <span className={ROW_VALUE_CLASS}>{concept}</span>
        </div>
      </div>

      <div className="text-xs leading-relaxed text-muted-foreground">
        {t("step3_irreversible_prefix")} <b className="text-foreground">{t("step3_irreversible_emphasis")}</b>
        .
      </div>
    </div>
  );
});
